import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, Home, ShieldAlert } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackSubtitle?: string;
  homeUrl?: string;
  homeLabel?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught unhandled rendering error:', error?.message || error);
    console.error('[ErrorBoundary] Component Stack:', errorInfo?.componentStack);
    console.error('[ErrorBoundary] Full Error Stack:', error?.stack);

    // Forward to error-tracking services (e.g. Sentry / custom window logger) if present
    if (typeof window !== 'undefined' && (window as any).Sentry?.captureException) {
      try {
        (window as any).Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
      } catch (e) {
        // Silently continue
      }
    }

    // Automatic self-healing for new deployments: if a chunk hash mismatch occurs, auto-reload cleanly once
    const errorMsg = error?.message || '';
    if (
      errorMsg.includes('Failed to fetch dynamically imported module') ||
      errorMsg.includes('dynamically imported module') ||
      errorMsg.includes('Loading chunk')
    ) {
      const lastAutoReload = sessionStorage.getItem('clm_chunk_reload');
      const now = Date.now();
      if (!lastAutoReload || now - parseInt(lastAutoReload, 10) > 10000) {
        sessionStorage.setItem('clm_chunk_reload', now.toString());
        this.handleReload();
        return;
      }
    }

    this.setState({ error, errorInfo });
  }

  public handleReload = () => {
    try {
      if (typeof window !== 'undefined') {
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach((name) => caches.delete(name));
          });
        }
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach((r) => r.unregister());
          });
        }
      }
    } catch {}
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.set('_reload', Date.now().toString());
    window.location.replace(cleanUrl.toString());
  };

  public handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    if (this.state.hasError) {
      const homeUrl = this.props.homeUrl || '/';
      const homeLabel = this.props.homeLabel || 'Accueil / Home';

      return (
        <div className="py-16 sm:py-24 px-4 bg-white flex items-center justify-center font-sans">
          <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 text-center shadow-sm">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200/80 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="w-7 h-7 text-[#094426]" />
            </div>

            <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 mb-2">
              {this.props.fallbackTitle || 'Contenu Temporairement Indisponible / Content Notice'}
            </h2>

            <p className="text-xs sm:text-sm text-slate-600 mb-6 leading-relaxed">
              {this.props.fallbackSubtitle ||
                'Une brève interruption est survenue. Cliquez sur Recharger pour actualiser instantanément.'}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={this.handleReload}
                className="w-full sm:w-auto px-5 py-2.5 bg-[#094426] hover:bg-[#0c5932] text-white font-bold rounded-xl text-xs sm:text-sm transition flex items-center justify-center gap-2 shadow-xs cursor-pointer"
              >
                <RefreshCw className="w-4 h-4 text-amber-300" />
                <span>Recharger / Reload</span>
              </button>

              <button
                type="button"
                onClick={() => (window.location.href = homeUrl)}
                className="w-full sm:w-auto px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs sm:text-sm transition flex items-center justify-center gap-2 cursor-pointer"
              >
                <Home className="w-4 h-4 text-slate-500" />
                <span>{homeLabel}</span>
              </button>
            </div>

            {this.state.error && (
              <details className="mt-4 text-left bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs font-mono text-rose-700">
                <summary className="cursor-pointer font-bold text-slate-700">Détails de l'erreur / Error Details</summary>
                <div className="mt-2 whitespace-pre-wrap">{this.state.error.message || String(this.state.error)}</div>
                {this.state.error.stack && (
                  <div className="mt-1 text-[10px] text-slate-500 overflow-x-auto whitespace-pre-wrap">{this.state.error.stack}</div>
                )}
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
